import { loadStripe, Stripe } from "@stripe/stripe-js";
import StripeServer from "stripe";
import { paymentConfig, validateStripeConfig, debugLog } from "./env";

// Client-side Stripe instance
let stripePromise: Promise<Stripe | null>;

export const getStripe = (): Promise<Stripe | null> => {
	if (!stripePromise) {
		debugLog("=== CLIENT STRIPE INITIALIZATION ===");
		debugLog("process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:", process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);
		debugLog("paymentConfig.stripe.publishableKey:", paymentConfig.stripe.publishableKey);
		debugLog("Publishable key:", paymentConfig.stripe.publishableKey ? "Present" : "Missing");
		debugLog("Publishable key value:", paymentConfig.stripe.publishableKey?.substring(0, 20) + "...");

		// Try direct access to environment variable as fallback
		const publishableKey = paymentConfig.stripe.publishableKey || process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
		debugLog("Final publishable key to use:", publishableKey ? "Present" : "Missing");

		if (!publishableKey) {
			console.error("No Stripe publishable key found");
			return Promise.resolve(null);
		}

		debugLog("Loading Stripe with publishable key...");
		stripePromise = loadStripe(publishableKey);
	}
	return stripePromise;
};

// Server-side Stripe instance
let stripeServer: StripeServer | null = null;

export const getStripeServer = (): StripeServer => {
	if (!stripeServer) {
		console.log("=== STRIPE SERVER INITIALIZATION ===");
		console.log("Checking Stripe configuration...");

		const config = validateStripeConfig();
		console.log("Stripe config validation:", config);

		if (!config.isValid) {
			console.error("Stripe configuration errors:", config.errors);
			throw new Error(`Stripe configuration errors: ${config.errors.join(", ")}`);
		}

		if (!paymentConfig.stripe.secretKey) {
			console.error("STRIPE_SECRET_KEY is missing");
			throw new Error("STRIPE_SECRET_KEY is required for server-side operations");
		}

		debugLog("Creating Stripe server instance...");
		debugLog("Secret key present:", !!paymentConfig.stripe.secretKey);
		debugLog("Secret key starts with:", paymentConfig.stripe.secretKey?.substring(0, 7));

		stripeServer = new StripeServer(paymentConfig.stripe.secretKey, {
			apiVersion: "2025-06-30.basil",
		});

		debugLog("Stripe server instance created successfully");
	}

	return stripeServer;
};

// Payment intent creation helper
export interface CreatePaymentIntentParams {
	amount: number; // in cents
	currency?: string;
	reservationId: string;
	customerEmail?: string;
	customerName?: string;
	description?: string;
	captureMethod?: "automatic" | "manual";
	metadata?: Record<string, string>;
}

export const createPaymentIntent = async (params: CreatePaymentIntentParams) => {
	console.log("=== STRIPE PAYMENT INTENT CREATION ===");
	console.log("Params:", params);

	try {
		const stripe = getStripeServer();
		debugLog("Stripe server instance created successfully");

		const {
			amount,
			currency = "eur",
			reservationId,
			customerEmail,
			customerName,
			description,
			captureMethod = "automatic",
			metadata = {},
		} = params;

		debugLog("Creating payment intent with Stripe API...");
		const paymentIntent = await stripe.paymentIntents.create({
			amount,
			currency,
			capture_method: captureMethod,
			metadata: {
				...metadata,
				reservationId,
				customerEmail: customerEmail || "",
				customerName: customerName || "",
			},
			description: description || `Payment for reservation ${reservationId}`,
			automatic_payment_methods: {
				enabled: true,
				allow_redirects: "never", // Prevent automatic redirects
			},
		});

		debugLog("Payment intent created successfully:", {
			id: paymentIntent.id,
			amount: paymentIntent.amount,
			currency: paymentIntent.currency,
			status: paymentIntent.status,
		});

		return {
			success: true,
			paymentIntent,
			clientSecret: paymentIntent.client_secret,
		};
	} catch (error) {
		console.error("=== STRIPE ERROR ===");
		console.error("Error creating payment intent:", error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "Unknown error",
		};
	}
};

// Payment confirmation helper
export const confirmPaymentIntent = async (paymentIntentId: string) => {
	const stripe = getStripeServer();

	try {
		const paymentIntent = await stripe.paymentIntents.retrieve(paymentIntentId);

		return {
			success: true,
			paymentIntent,
			status: paymentIntent.status,
		};
	} catch (error) {
		console.error("Error confirming payment intent:", error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "Unknown error",
		};
	}
};

// Webhook signature verification
export const verifyWebhookSignature = (
	payload: string | Buffer,
	signature: string
): { success: boolean; event?: StripeServer.Event; error?: string } => {
	if (!paymentConfig.stripe.webhookSecret) {
		return {
			success: false,
			error: "Webhook secret not configured",
		};
	}

	try {
		const stripe = getStripeServer();
		const event = stripe.webhooks.constructEvent(payload, signature, paymentConfig.stripe.webhookSecret);

		return {
			success: true,
			event,
		};
	} catch (error) {
		console.error("Webhook signature verification failed:", error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "Signature verification failed",
		};
	}
};

// Format amount for display (convert cents to euros)
export const formatAmount = (amountInCents: number, currency = "EUR"): string => {
	const amount = amountInCents / 100;
	return new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency,
	}).format(amount);
};

// Convert euros to cents for Stripe
export const eurosToCents = (euros: number): number => {
	return Math.round(euros * 100);
};

// Payment status helpers
export const isPaymentSuccessful = (status: string): boolean => {
	return status === "succeeded";
};

export const isPaymentPending = (status: string): boolean => {
	return ["processing", "requires_action", "requires_confirmation"].includes(status);
};

export const isPaymentFailed = (status: string): boolean => {
	return ["canceled", "payment_failed", "requires_payment_method"].includes(status);
};
